import {DB_COLLECTION_NAMES, LOCAL_STORAGE_KEYS, PAGE_URLS, SIGNIN_TYPES} from "constants/AppConstants.js";
import {useAuth} from "contexts/AuthContext";
import {DbCollectionFactory} from "factories/DbCollectionFactory";
import {useHistory} from "react-router-dom";
import AuthService from "services/AuthService.js";
import LocalStorageService from "services/LocalStorageService.js";
import ToastService from "services/ToastService.js";
import {customLogEvent, isAndroid} from "FirebaseUtils";
import {useAppUser} from "contexts/AppUserContext";
import {useWebPaywall} from "contexts/WebPaywallContext";
import {Capacitor, registerPlugin} from "@capacitor/core";
import {useTranslation} from "react-i18next";
import type {CheckoutTier} from "services/WebCheckoutService";

export interface ShowPaywallOptions {
  /** Tier the gate needs. Preselected in the web plan picker; Android's native paywall picks its own. */
  tier?: CheckoutTier;
}

const Paywall = registerPlugin<any>("PaywallPlugin");
const OnboardingPlugin = registerPlugin<any>("OnboardingPlugin");
const hasNativeOnboarding = () => Capacitor.isPluginAvailable("OnboardingPlugin");

const swiftTimestampInSeconds = new Date(Date.UTC(2001, 0, 1, 0, 0, 0)).getTime() / 1000; // Swift timestamp reference

const useSignIn = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const {setIsAuthenticating, setIsGettingClaims} = useAuth();
  const {updateSubscriptionExpiryDateFromAdapty, appUser, setForceRefresh} = useAppUser();
  const {openWebPaywall} = useWebPaywall();

  function handleFailureSignIn(error: any) {
    const {code, message} = error;

    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      return;
    }

    ToastService.error(message, "top-right");
    return message;
  }

  function handleSuccessSignIn(signInType: any, result: any) {
    try {
      LocalStorageService.set(LOCAL_STORAGE_KEYS.signInType, signInType);
      LocalStorageService.set(LOCAL_STORAGE_KEYS.uid, result.user.uid);
      return true;
    } catch (error) {
      LocalStorageService.clear();
      return false;
    }
  }

  async function signOutAndRedirectAsync(redirectUrl: string) {
    await AuthService.signOutAsync(isAndroid);
    LocalStorageService.clear();
    setIsAuthenticating(false);
    setIsGettingClaims(false);
    history.push(redirectUrl);
  }

  async function handlerSignIn(
    signInType: any,
    credential: any = {},
    signUp = false,
    options: {stayOnPage?: boolean} = {}
  ) {
    // On Android, authentication is fully handled by native code
    if (isAndroid) {
      return true;
    }

    let result: any = await AuthService.signInAsync(signInType, credential, isAndroid);
    if (result.isFailure && !signUp && options.stayOnPage) {
      // Redirecting to the sign-in page would lose the wizard (and a payment link).
      const code = result.error?.code;
      const wrongCredentials = ["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"];
      if (wrongCredentials.includes(code)) {
        ToastService.error(t("messages.invalidCredential"), "top-right");
      } else {
        handleFailureSignIn(result.error);
      }
      return;
    }
    if (result.isFailure && !signUp) {
      handleFailureSignIn(result.error);
      await signOutAndRedirectAsync(PAGE_URLS.signInPageUrl);
      return;
    }

    setIsAuthenticating(true);

    const userResult = result?.data?.user;
    let uid = userResult?.uid;

    const userCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.users);
    let user;
    let requiresPostOnboarding = true;
    let isNewUser = false;

    if (signInType === SIGNIN_TYPES.apple) {
      await AuthService.signOutAsync(isAndroid);
      const credential = AuthService.createCredentialFromAppleLoginAsync(result);
      const resultFakeApple = await AuthService.signInAsync(SIGNIN_TYPES.emailAndPassword, credential, isAndroid);
      if (resultFakeApple.isFailure) {
        // Reconnect with real apple flow
        result = await AuthService.signWithRealAppleFlow(result);
        user = await userCollection.getUserByIdAsync(uid, true, true);
        if (!user) {
          handleFailureSignIn(result.error || resultFakeApple.error);
          await signOutAndRedirectAsync(PAGE_URLS.signInPageUrl);
          return;
        }
      } else {
        result = resultFakeApple;
        uid = result.data.user.uid;
        user = await userCollection.getUserByIdAsync(uid, true, true);
      }
    } else {
      // A failed sign-in leaves uid undefined — never look up "users/undefined" in RTDB
      user = uid ? await userCollection.getUserByIdAsync(uid, true, true) : null;
    }

    if (!user && !signUp) {
      await signOutAndRedirectAsync(PAGE_URLS.unauthorizedPageUrl);
      return;
    } else if (!user && signUp) {
      let currentDate = new Date();
      const newUser = {
        email:
          result?.data?.user?.email || result?.data?.additionalUserInfo?.profile?.email || credential.email || null,
        name: result?.data?.user?.displayName || credential.name || null,
        firstLogin: currentDate.getTime() / 1000,
        firstLoginFull: currentDate.toISOString(),
        subscriptionExpDate: null,
        subscriptionExpDateFull: null,
        lmd: currentDate.getTime() / 1000,
        lmdFull: currentDate.toISOString(),
        createdFromAndroid: isAndroid
      };
      if (signInType === SIGNIN_TYPES.emailAndPassword) {
        result = await AuthService.createUserWithEmailAndPassword(newUser.email, credential.password, isAndroid);
        if (result?.data?.user) {
          uid = result.data.user.uid;
        } else if (result?.error?.message) {
          if (options.stayOnPage) {
            // Redirecting here would drop the wizard's state and empty the form.
            ToastService.error(
              result.error.code === "auth/email-already-in-use" ? t("signIn.emailAlreadyExists") : result.error.message,
              "top-right"
            );
            setIsAuthenticating(false);
            return;
          }
          handleFailureSignIn(result.error);
          await signOutAndRedirectAsync(PAGE_URLS.signInPageUrl);
          return;
        }
      }

      user = await userCollection.setUserAsync(uid, newUser);
      isNewUser = true;
      setForceRefresh((prev) => prev + 1);

      if (!user) {
        ToastService.error(t("signIn.createUserError"), "top-right");
        return;
      } else {
        requiresPostOnboarding = false;
        // The web onboarding wizard replaces the native post-onboarding view
        const onboardedUser =
          options.stayOnPage || !hasNativeOnboarding()
            ? null
            : await showPostOnboarding(newUser, signInType !== SIGNIN_TYPES.emailAndPassword);
        if (onboardedUser) {
          // date of birth is based on swift timestamp
          currentDate = new Date();
          const userDetails = {
            ...newUser,
            name: onboardedUser.name || null,
            dateOfBirth: new Date(onboardedUser.birthDate).getTime() / 1000 - swiftTimestampInSeconds || null,
            country: onboardedUser.country || null,
            weight: onboardedUser.weight || null,
            height: onboardedUser.height || null,
            sports: onboardedUser.sports ?? null,
            marketing: onboardedUser.marketingConsent || null,
            gender: onboardedUser.gender || null,
            lmd: currentDate.getTime() / 1000,
            lmdFull: currentDate.toISOString()
          };

          await userCollection.setUserAsync(uid, userDetails);
          setForceRefresh((prev) => prev + 1);
        }
      }
    } else if (user && signUp && !options.stayOnPage) {
      await signOutAndRedirectAsync(PAGE_URLS.signInPageUrl);
      ToastService.warning(t("signIn.emailAlreadyExists"), "top-right");
      return;
    }

    if (options.stayOnPage && !uid) {
      // Auth did not actually complete (e.g. network/App Check failure) — don't let the wizard advance
      handleFailureSignIn(result?.error || {message: t("signIn.createUserError")});
      setIsAuthenticating(false);
      return;
    }

    handleSuccessSignIn(signInType, result.data);
    setIsAuthenticating(false);
    if (options.stayOnPage) {
      // The onboarding wizard drives navigation itself ("Create an account or login" allows both)
      return {ok: true, isNewUser};
    }
    if (requiresPostOnboarding && hasNativeOnboarding()) {
      await OnboardingPlugin.showPostOnboardingView({screen: "onboardingPermission"});
    }
    history.push(PAGE_URLS.diaryPageUrl);
    return true;
  }

  /**
   * Opens the paywall for the current platform: the native Adapty paywall on
   * Android, the Stripe plan picker on the web. The iOS webview is not handled
   * here — it has to ask the host app through a WebKit message, which only the
   * components holding that bridge can send.
   */
  const showPaywall = async ({tier = "premium"}: ShowPaywallOptions = {}) => {
    if (!isAndroid) {
      openWebPaywall(tier);
      return;
    }

    console.log("Show Paywall");
    try {
      const result = await Paywall.showPaywall();
      await updateSubscriptionExpiryDateFromAdapty();
      console.log("Paywall result:", result.value);
    } catch (error) {
      console.error("Error showing paywall:", error);
    }
  };

  const showPaywallIfNeeded = async () => {
    if (appUser?.isUserPro || !isAndroid) {
      // no need to show paywall
      return true;
    }
    customLogEvent("Paywall Showed", undefined);
    await showPaywall();
    customLogEvent("Paywall Closed", {isPro: appUser.isUserPro});
    return appUser.isUserPro;
  };

  async function showPostOnboarding(data: any, shouldAcceptTerms: boolean) {
    console.log("Show Post Onboarding");
    try {
      const result = await OnboardingPlugin.showPostOnboardingView({data, shouldAcceptTerms});
      console.log("Post Onboarding result:", result);
      return result;
    } catch (error) {
      console.error("Error showing post onboarding:", error);
      return null;
    }
  }

  return {handlerSignIn, showPaywall, showPaywallIfNeeded};
};

export default useSignIn;
